import React, {FC, useEffect, useState} from 'react'
import {useNavigate, useSearchParams, useLocation} from 'react-router-dom'
import useAppToast from 'src/hooks/useAppToast'
import {fetchUserAccountDetailsAsync} from 'src/redux/actions/accountActions'
import {fetchConnectedPlatformAsync, getPlatformAdditionalInfoStatusAsync, getPlatformOwnerFieldsAsync} from 'src/redux/actions/platformActions'
import {
  handleOauthCallbackAsync,
  refreshTokenOauthUrlHandleAsync,
  userOauthLoginAsync
} from 'src/redux/actions/authActions'
import {setShopifyConnectFirstTimeMessageShow} from 'src/redux/slices/authSlice'
import {setPlatformSettingModalData} from 'src/redux/slices/connectedPlatformSlice'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import Loader from 'src/utils/Loader'
import ZohoOrgSelectionModal from './ZohoOrgSelectionModal'
import {
  accessSessionTokenKeyName,
  checkUserToNavigateInPricing,
  handleRoutePathOnOauth,
  refreshOauthTokenData,
  refreshSessionTokenKeyName,
  shopifyDetailsForOauth,
  shopifyDetailsForWithoutAuth,
} from 'src/utils/constants'
import {
  clearPendingPlatformInstallation,
  getPlatformInstallationStorageKey,
  getSupportedInstallablePlatform,
} from 'src/utils/tiktokFinalizer'
import {finalizeTikTokAfterAuth} from 'src/utils/finalizeTikTokAfterAuth'
import {
  clearWalmartMarketplaceCallbackContext,
  getPendingWalmartMarketplaceOauthParams,
} from 'src/utils/walmartMarketplaceHandoff'
import {messageTemplete} from 'src/utils/messageTemplete'
import {
  consumeImpersonationReturnPath,
  isImpersonationSessionActive,
  setActiveSessionTokensPreservingImpersonation,
} from 'src/utils/impersonationTokens'
import {isSecondaryConnectedPlatform, isSourceConnectedPlatform} from 'src/utils/platformConnectionValidation'
import {needsPostConnectChannelSettings} from 'src/utils/platformAdditionalInfo'

const ZOHO_PLATFORMS = ['ZohoInventory']

const HandleOauth: FC = () => {
  const [loading, setLoading] = useState(true)
  const [showZohoOrgModal, setShowZohoOrgModal] = useState(false)
  const [zohoConnectedPlatformData, setZohoConnectedPlatformData] = useState<any>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [userId, setUserId] = useState<any>()
  const dispatch = useAppDispatch()
  const {errorToast, successToast, warningToast} = useAppToast()
  const navigate = useNavigate()
  const location = useLocation()

  const openChannelSettingsAfterConnect = async (connectedPlatform: any) => {
    const {id, name, platform_info} = connectedPlatform
    localStorage.setItem(`platformFirstConnect_${name}_${id.toString()}`, 'true')
    localStorage.removeItem(handleRoutePathOnOauth)
    await dispatch(fetchConnectedPlatformAsync())
    const availablePlatformId =
      typeof platform_info === 'object' ? platform_info?.id : platform_info
    navigate(`/channel-settings/${availablePlatformId}/${id}`)
  }

  const routeAfterSuccessfulConnect = async (
    connectedPlatform: any,
    impersonationReturnPath: string | null = null
  ) => {
    if (localStorage.getItem(handleRoutePathOnOauth)) {
      localStorage.removeItem(handleRoutePathOnOauth)
      navigate('/listing-channels')
      return
    }
    let statusPayload: unknown
    let ownerFieldsPayload: unknown

    try {
      const [statusResponse, ownerFieldsResponse] = await Promise.all([
        dispatch(
          getPlatformAdditionalInfoStatusAsync({platform_id: String(connectedPlatform.id)})
        ),
        dispatch(
          getPlatformOwnerFieldsAsync({
            platform_id: String(connectedPlatform.id),
            field_id: '',
          })
        ),
      ])
      statusPayload = statusResponse?.payload
      ownerFieldsPayload = ownerFieldsResponse?.payload
    } catch {
      statusPayload = null
      ownerFieldsPayload = null
    }

    const shouldOpenChannelSettings =
      isSecondaryConnectedPlatform(connectedPlatform) ||
      isSourceConnectedPlatform(connectedPlatform) ||
      needsPostConnectChannelSettings({
        connectedPlatform,
        statusPayload,
        ownerFieldsPayload,
      })

    if (shouldOpenChannelSettings) {
      await openChannelSettingsAfterConnect(connectedPlatform)
      return
    }

    if (impersonationReturnPath) {
      setLoading(false)
      navigate(impersonationReturnPath)
      return
    }

    await openChannelSettingsAfterConnect(connectedPlatform)
  }

  useEffect(() => {
    // Dispatch the fetch user data action on component mount
    dispatch(fetchUserAccountDetailsAsync())
      .then((response) => {
        setUserId(response.payload) // Log the response data
      })
      .catch((error) => {
      })
  }, [])

  useEffect(() => {
    const code = searchParams.get('code')
    const hmac = searchParams.get('hmac')
    const host = searchParams.get('host')
    const shop = searchParams.get('shop')
    const state = searchParams.get('state')
    const shop_region = searchParams.get('shop_region')
    const locale = searchParams.get('locale')
    const app_key = searchParams.get('app_key')
    const timestamp = searchParams.get('timestamp')
    const spapi_oauth_code = searchParams.get('spapi_oauth_code')
    const selling_partner_id = searchParams.get('selling_partner_id')
    const store_id = searchParams.get('store_id')
    const platform_name = searchParams.get('platform_name')
    const appid = searchParams.get('appid')
    const tempToken = searchParams.get('tempToken')
    const checkAvailabilityForPricing = localStorage.getItem(checkUserToNavigateInPricing)
    const pendingWalmartOauthParams = getPendingWalmartMarketplaceOauthParams()
    const isPendingWalmartCallback =
      (pendingWalmartOauthParams?.available_platform_name || '').toUpperCase() === 'WALMART'
    const restrictedOnboardingPlatform =
      getSupportedInstallablePlatform(platform_name) || (isPendingWalmartCallback ? 'WALMART' : null)
    const isRestrictedOnboardingFlow = Boolean(restrictedOnboardingPlatform)
    const isRestrictedRoute = location.pathname.includes('handle-oauth-callback-restricted')

    const hasConnectedWalmartPlatform = (payload: any) => {
      const candidateData = payload?.data?.data || payload?.data || payload?.results || payload
      const platformList = Array.isArray(candidateData)
        ? candidateData
        : Array.isArray(candidateData?.results)
        ? candidateData.results
        : []

      return platformList.some((platform: any) => {
        const normalizedName = `${
          platform?.name ||
          platform?.code ||
          platform?.display_name ||
          platform?.platform_info?.name ||
          platform?.platform_info?.code ||
          ''
        }`.toUpperCase()

        return normalizedName === 'WALMART' || normalizedName.includes('WALMART')
      })
    }

    const fetchIsWalmartConnected = async () => {
      const connectedPlatformResponse = await dispatch(fetchConnectedPlatformAsync())
      return hasConnectedWalmartPlatform(connectedPlatformResponse?.payload)
    }

    //this will get all query parameters dynamically
    let urlQueryParams = {}
    if (window.location.search) {
      let queryItems = window.location.search.slice(1, window.location.search.length).split('&')
      for (let ser of queryItems) {
        let keyval = ser.split('=')
        urlQueryParams[keyval[0]] = decodeURIComponent(keyval[1] || '')
      }
    }
    // -------------------------------- //

    const persistRestrictedOnboardingPayload = (platformResponseData: any) => {
      if (!restrictedOnboardingPlatform) {
        return null
      }

      const userEmail = platformResponseData?.email ?? ''
      const resolvedPlatformData =
        platformResponseData?.platform_data ??
        platformResponseData?.platform_data_str ??
        platformResponseData?.data ??
        (restrictedOnboardingPlatform === 'WALMART' ? platformResponseData : null)
      const storedPayload = {
        platform_name: platformResponseData?.platform_name || restrictedOnboardingPlatform,
        platform_data: resolvedPlatformData,
        email: userEmail ?? '',
        username: platformResponseData?.username ?? '',
        name: platformResponseData?.name ?? platformResponseData?.username ?? '',
        ...(restrictedOnboardingPlatform === 'WALMART'
          ? {
              available_platform_name: pendingWalmartOauthParams?.available_platform_name || 'WALMART',
              user_id: pendingWalmartOauthParams?.user_id,
              platform_id:
                pendingWalmartOauthParams?.platform_id ||
                platformResponseData?.platform_id ||
                platformResponseData?.available_platform_id,
              country_domain: pendingWalmartOauthParams?.country_domain,
              marketplace: pendingWalmartOauthParams?.marketplace,
              proxies: pendingWalmartOauthParams?.proxies,
              identifier: pendingWalmartOauthParams?.identifier,
            }
          : {}),
      }

      try {
        localStorage.setItem(
          getPlatformInstallationStorageKey(restrictedOnboardingPlatform),
          JSON.stringify(storedPayload)
        )
      } catch (error) {
      }

      return storedPayload
    }

    const navigateToRestrictedRegistration = (storedPayload: any) => {
      if (!restrictedOnboardingPlatform) {
        return
      }

      const registrationParams = new URLSearchParams()
      registrationParams.append('source', restrictedOnboardingPlatform)
      if (storedPayload?.email) {
        registrationParams.append('email', storedPayload.email)
      }
      const inferredName = storedPayload?.name || urlQueryParams['name']
      if (inferredName) {
        registrationParams.append('name', inferredName)
      }
      if (storedPayload?.username) {
        registrationParams.append('username', storedPayload.username)
      }
      setLoading(false)
      navigate(`/auth/registration?${registrationParams.toString()}`)
    }

    const navigateToRestrictedAuth = (storedPayload: any) => {
      if (restrictedOnboardingPlatform === 'WALMART') {
        const loginParams = new URLSearchParams()
        loginParams.append('source', 'WALMART')
        if (storedPayload?.email) {
          loginParams.append('email', storedPayload.email)
        }
        setLoading(false)
        navigate(`/auth/login?${loginParams.toString()}`)
        return
      }

      navigateToRestrictedRegistration(storedPayload)
    }

    const completeRestrictedOnboardingFlow = async (platformResponseData: any) => {
      const storedPayload = persistRestrictedOnboardingPayload(platformResponseData)

      if (
        restrictedOnboardingPlatform === 'WALMART' &&
        localStorage.getItem(accessSessionTokenKeyName)
      ) {
        try {
          if (await fetchIsWalmartConnected()) {
            clearPendingPlatformInstallation('WALMART')
            successToast('Walmart connected successfully.')
            await dispatch(fetchUserAccountDetailsAsync())
            setLoading(false)
            navigate('/')
            return
          }

          const didFinalize = await finalizeTikTokAfterAuth(dispatch)
          const isConnectedAfterFinalize = await fetchIsWalmartConnected()
          if (!didFinalize && !isConnectedAfterFinalize) {
            errorToast('Unable to finalize Walmart installation. Please try again.')
            setLoading(false)
            return
          }
          clearPendingPlatformInstallation('WALMART')
          await dispatch(fetchUserAccountDetailsAsync())
          successToast('Walmart connected successfully.')
          setLoading(false)
          navigate('/')
        } catch (error) {
          setLoading(false)
          errorToast('Unable to finalize Walmart installation. Please try again.')
        }
        return
      }

      navigateToRestrictedAuth(storedPayload)
    }

    const pltfrmdetail = localStorage.getItem(shopifyDetailsForOauth);
    const platfrmDtlWithoutAuth = localStorage.getItem(shopifyDetailsForWithoutAuth)
    const refreshOauthData = localStorage.getItem(refreshOauthTokenData)
    
    //this condition always work after login so no need to check user data
    if (pltfrmdetail) {
      const parsedDetail = JSON.parse(pltfrmdetail)
      const walmartOauthMappedFields = Object.entries(parsedDetail ?? {}).reduce(
        (acc: Record<string, any>, [key, value]) => {
          const normalizedKey = key.toLowerCase()
          const isWalmartCountryDomainField =
            normalizedKey.includes('country') && normalizedKey.includes('domain')
          const isWalmartMarketplaceField = normalizedKey.includes('marketplace')

          if ((isWalmartCountryDomainField || isWalmartMarketplaceField) && value !== undefined) {
            acc[key] = value
          }

          return acc
        },
        {}
      )
      //  dispatch(handleOauthCallbackAsync({  store_id : store_id ?? '',  shop_region : shop_region ? shop_region : '', locale:locale ? locale : '',  app_key: app_key ? app_key : '', code,hmac,host,shop, state, timestamp, isMainStore: parsedDetail?.isStoreCreated ? 0 : 1  , user_id:parsedDetail?.userId, available_platform_name:parsedDetail?.name, display_name: parsedDetail?.friendlyName, spapi_oauth_code : spapi_oauth_code ? spapi_oauth_code :'',selling_partner_id: selling_partner_id ? selling_partner_id : '', auth_domain : parsedDetail?.zohoAuthDomain ? parsedDetail?.zohoAuthDomain : ''  }))
      dispatch(
        handleOauthCallbackAsync({
          isMainStore: parsedDetail?.isMainStore === true ? 1 : 0,
          isAccountingStore: parsedDetail?.isAccountingStore  === true ? 1 : 0,
          isShippingStore: parsedDetail?.isShippingStore  === true ? 1 : 0,
          isErpStore: parsedDetail?.isErpStore === true ? 1 : 0,
          user_id: parsedDetail?.userId,
          available_platform_name: parsedDetail?.name,
          display_name: parsedDetail?.friendlyName,
          auth_domain: parsedDetail?.zohoAuthDomain ? parsedDetail?.zohoAuthDomain : '',
          ...(parsedDetail?.name === 'EBAY'
            ? {country_iso: parsedDetail?.country_iso}
            : {country: parsedDetail?.country}),
          client_id:parsedDetail['client_id'], 
          client_secret:parsedDetail['client_secret'],
          proxies:parsedDetail['proxies'],
          version:parsedDetail['version'],
          country_domain: parsedDetail?.country_domain,
          marketplace: parsedDetail?.marketplace,
          platform_id: parsedDetail?.platform_id ?? urlQueryParams['platform_id'] ?? null,
          ...walmartOauthMappedFields,
          ...urlQueryParams,
        })
      ).then((response) => {
        if (response.payload?.status_code == 400) {
          errorToast(response.payload?.message || response.payload?.massage)
          const impersonationReturnPath = isImpersonationSessionActive()
            ? consumeImpersonationReturnPath()
            : null
          navigate(impersonationReturnPath ?? '/auth')
        }

        if (response.payload?.status_code === 200) {
          successToast('Channel Connected Successfully')
          localStorage.removeItem(shopifyDetailsForOauth)

          const impersonationReturnPath = isImpersonationSessionActive()
            ? consumeImpersonationReturnPath()
            : null

          if (response.payload?.data?.id) {
            // Show org selection popup for Zoho platforms before navigating to settings
            if (ZOHO_PLATFORMS.includes(parsedDetail?.name)) {
              setZohoConnectedPlatformData(response.payload.data)
              setShowZohoOrgModal(true)
              setLoading(false)
              return
            }

            if (checkAvailabilityForPricing) {
              localStorage.removeItem(checkUserToNavigateInPricing)
              navigate(`/pricing/${response.payload?.data?.id}`)
            } else {
              void routeAfterSuccessfulConnect(response.payload.data, impersonationReturnPath)
            }
          } else if (impersonationReturnPath) {
            setLoading(false)
            navigate(impersonationReturnPath)
          }

        }
        // OAuth Deny Handling
        else if (response.payload?.status_code == 207) {
          errorToast(response.payload?.massage)
          const impersonationReturnPath = isImpersonationSessionActive()
            ? consumeImpersonationReturnPath()
            : null
          navigate(impersonationReturnPath ?? '/listing-channels')
        }else{
          warningToast('Unable to connect' + response?.payload?.massage) 
        }
      })
    } else if (platfrmDtlWithoutAuth) {
      let parsedPltDtl = JSON.parse(platfrmDtlWithoutAuth)
      let payload: any = {}
      let shopifyPlatform = parsedPltDtl?.platform_name
      if (parsedPltDtl?.user_id) {
        payload = {
          available_platform_name: parsedPltDtl?.platform_name,
          display_name: parsedPltDtl?.platform_name,
          user_id: parsedPltDtl?.user_id,
          ...urlQueryParams,
        }
      } else {
        payload = {
          available_platform_name: parsedPltDtl?.platform_name,
          display_name: parsedPltDtl?.platform_name,
          ...urlQueryParams,
        }
      }
      
      if (isRestrictedOnboardingFlow) {
        const normalizedName = restrictedOnboardingPlatform || ''
        payload = {
          ...(pendingWalmartOauthParams || {}),
          ...payload,
        }
        payload.platform_name = normalizedName
        payload.available_platform_name = payload.available_platform_name || normalizedName
        payload.display_name = payload.display_name || normalizedName
        const ensureValue = (field: string) => {
          if (!payload[field]) {
            payload[field] = `${field}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
          }
        }
        const fallbackSource: Record<string, string | null> = {
          code,
          app_key,
          shop_region,
          locale,
          state,
          timestamp,
          store_id,
          selling_partner_id,
          spapi_oauth_code,
          host,
          hmac,
          shop,
          appid,
          tempToken,
        }
        Object.entries(fallbackSource).forEach(([key, value]) => {
          if (value && !payload[key]) {
            payload[key] = value
          }
        })
        ;[
          'code',
          'app_key',
          'shop_region',
          'locale',
          'state',
          'timestamp',
          'store_id',
          'selling_partner_id',
          'spapi_oauth_code',
          'host',
          'hmac',
          'shop',
          'appid',
          'tempToken',
        ].forEach(ensureValue)
      }

        dispatch(handleOauthCallbackAsync(payload)).then(async (response1) => {
          if (restrictedOnboardingPlatform === 'WALMART') {
            clearWalmartMarketplaceCallbackContext()
          }
          if (response1.payload?.status_code == 400) {
            errorToast(response1.payload?.message || response1.payload?.massage)
            if (isRestrictedRoute && isRestrictedOnboardingFlow) {
              navigate(
                restrictedOnboardingPlatform === 'WALMART'
                  ? '/auth/login?source=WALMART'
                  : `/auth/registration?source=${restrictedOnboardingPlatform}`
              )
            } else {
            navigate('/auth')
          }
        }
        if (response1.payload?.status_code === 200) {
          localStorage.removeItem(shopifyDetailsForWithoutAuth)
          
          if (isRestrictedRoute && isRestrictedOnboardingFlow) {
            await completeRestrictedOnboardingFlow(response1.payload?.data || response1.payload)
            return
          }

        dispatch(
            userOauthLoginAsync({
              email: response1.payload?.data?.user?.email,
              // password: response.payload?.data?.credentials?.identifier
            })
          )
            .then((response) => {
              if (response?.payload?.status === 401) {
                errorToast(response?.payload?.detail)
                setLoading(false)
              } else if (response?.payload?.access) {
                setActiveSessionTokensPreservingImpersonation(
                  response.payload.access,
                  response.payload.refresh
                )
                if (shopifyPlatform === 'SHOPIFY') {
                  successToast('Shopify connected successfully.')
                } else {
                  successToast('Success')
                }
                setLoading(false)
                dispatch(setShopifyConnectFirstTimeMessageShow({show: true}))
                if (checkAvailabilityForPricing) {
                  localStorage.removeItem(checkUserToNavigateInPricing)
                  navigate(`/pricing/${response1.payload?.data?.id}`)
                } else {
                  navigate('/auth')
                }
              } else {
                errorToast(messageTemplete.SOMETHING_WENT_WRONG)
                setLoading(false)
              }
            })
            .catch((error) => {
              setLoading(false)
              navigate('/auth')
            })
        } 
        // OAuth Deny Handling
        else if (response1.payload?.status_code == 207) {
          errorToast(response1.payload?.massage)
          navigate('/auth')
        }else{
          warningToast('Unable to connect' +  response1?.payload?.massage)
        }
      })
    } else {
      if(refreshOauthData){
        const refreshOauthParsedData = JSON.parse(refreshOauthData) 
      dispatch(refreshTokenOauthUrlHandleAsync({available_platform_name: refreshOauthParsedData?.available_platform_name, user_id: refreshOauthParsedData?.user_id }))
      .then((res)=>{
        if (res.payload?.status_code === 200) {
          successToast('Refresh token updated successfully')
          localStorage.removeItem(refreshOauthData)
          navigate('/listing-channels')
        }
        setLoading(false)
      })
      .catch((error) => {
        localStorage.removeItem(refreshOauthData)
        errorToast('Refresh token not updated. ')
        setLoading(false)
        navigate('/auth')
      })

      }else{
        //for eqwid, wix cases
        const payload: any = {...urlQueryParams}
        if (isRestrictedOnboardingFlow) {
          const normalizedName = restrictedOnboardingPlatform || ''
          Object.assign(payload, {
            ...(pendingWalmartOauthParams || {}),
            ...payload,
          })
          payload.platform_name = normalizedName
          if (!payload.available_platform_name) {
            payload.available_platform_name = normalizedName
          }
          if (!payload.display_name) {
            payload.display_name = normalizedName
          }
          if (!payload.code && code) payload.code = code
          if (!payload.app_key && app_key) payload.app_key = app_key
          if (!payload.shop_region && shop_region) payload.shop_region = shop_region
          if (!payload.locale && locale) payload.locale = locale
          if (!payload.state && state) payload.state = state
          if (!payload.timestamp && timestamp) payload.timestamp = timestamp
          if (!payload.store_id && store_id) payload.store_id = store_id
          if (!payload.selling_partner_id && selling_partner_id) payload.selling_partner_id = selling_partner_id
          if (!payload.spapi_oauth_code && spapi_oauth_code) payload.spapi_oauth_code = spapi_oauth_code
          if (!payload.host && host) payload.host = host
          if (!payload.hmac && hmac) payload.hmac = hmac
          if (!payload.shop && shop) payload.shop = shop
          if (!payload.appid && appid) payload.appid = appid
          if (!payload.tempToken && tempToken) payload.tempToken = tempToken
          const ensureValue = (field: string) => {
            if (!payload[field]) {
              payload[field] = `${field}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
            }
          }
          ;[
            'code',
            'app_key',
            'shop_region',
            'locale',
            'state',
            'timestamp',
            'store_id',
            'selling_partner_id',
            'spapi_oauth_code',
            'host',
            'hmac',
            'shop',
            'appid',
            'tempToken',
          ].forEach(ensureValue)
          
        }

        dispatch(handleOauthCallbackAsync(payload)).then(async (response1) => {
          if (restrictedOnboardingPlatform === 'WALMART') {
            clearWalmartMarketplaceCallbackContext()
          }
          if (response1.payload?.status_code == 400) {
            errorToast(response1.payload?.message || response1.payload?.massage)
            if (isRestrictedRoute && isRestrictedOnboardingFlow) {
              navigate(
                restrictedOnboardingPlatform === 'WALMART'
                  ? '/auth/login?source=WALMART'
                  : `/auth/registration?source=${restrictedOnboardingPlatform}`
              )
            } else {
              navigate('/auth')
            }
          }
          if (response1.payload?.status_code === 200) {
            localStorage.removeItem(shopifyDetailsForWithoutAuth)

            if (isRestrictedRoute && isRestrictedOnboardingFlow) {
              await completeRestrictedOnboardingFlow(response1.payload?.data || response1.payload)
              return
            }

          dispatch(
            userOauthLoginAsync({
              email: response1.payload?.data?.user?.email,
            })
          )
              .then((response) => {
                if (response?.payload?.status === 401) {
                  errorToast(response?.payload?.detail)
                  setLoading(false)
                } else if (response?.payload?.access) {
                  setActiveSessionTokensPreservingImpersonation(
                    response.payload.access,
                    response.payload.refresh
                  )
                  if (response1?.payload?.data?.user?.source == 'WIX') {
                    successToast('WIX connected successfully.')
                  } else if (response1?.payload?.massage) {
                    successToast(response1.payload.massage)
                  } else {
                    successToast('Ecwid connected successfully.')
                  }
                  setLoading(false)
                  dispatch(setShopifyConnectFirstTimeMessageShow({show: true}))
                  if (['WIX'].includes(response1?.payload?.data?.user?.source)) {
                    navigate(`/pricing`)
                  } else {
                    navigate(`/listing-channels`)
                  }
                } else {
                  errorToast(messageTemplete.SOMETHING_WENT_WRONG)
                  setLoading(false)
                }
              })
              .catch((error) => {
                setLoading(false)
                navigate('/auth')
              })
          }
          // OAuth Deny Handling
          else if (response1.payload?.status_code == 207) {
            errorToast(response1.payload?.massage)
            navigate('/auth')
          } else {
            warningToast('Unable to connect' + response1?.payload?.massage)
          }
        })
      }
    }
  }, [])

  return (
    <div className='d-flex align-items-center justify-content-center'>
      {loading ? <Loader variant="section" /> : null}
      {showZohoOrgModal && zohoConnectedPlatformData && (
        <ZohoOrgSelectionModal
          show={showZohoOrgModal}
          platformData={zohoConnectedPlatformData}
          onSuccess={() => {
            void openChannelSettingsAfterConnect(zohoConnectedPlatformData)
          }}
        />
      )}
    </div>
  )
}

export default HandleOauth

